import {
  Object3D,
  PlaneGeometry,
  Vector2,
  Vector3,
  Quaternion,
  Euler,
  Mesh,
  BufferGeometry
} from 'three';
import { Color4 } from '../types/color4';
import { ParticleSystemData } from '../loaders/ptl';

export class Particle {
  rootObj: Object3D;
  age: number = 0;
  lifetime: number = 1;
  eventIndex: number = 0;
  eventTimer: number = 0;
  position: Vector3;
  gravity: Vector3;
  size: Vector2;
  sizeStep: Vector2;
  textureIndex: number = 0;
  textureIndexStep: number = 0;
  textureColumns: number = 1;
  textureRows: number = 1;
  color: Color4;
  colorStep: Color4;
  velocity: Vector3;
  velocityStep: Vector3;
  rotation: number = 0;
  rotationStep: number = 0;
  mesh: any;
  alignType: any;

  constructor() {
    this.rootObj = new Object3D();
    this.position = new Vector3();
    this.gravity = new Vector3();
    this.size = new Vector2(10, 10);
    this.sizeStep = new Vector2();
    this.color = new Color4(1, 1, 1, 1);
    this.colorStep = new Color4();
    this.velocity = new Vector3();
    this.velocityStep = new Vector3();
  }

  reset(): void {
    this.age = 0;
    this.lifetime = 1;
    this.eventIndex = 0;
    this.eventTimer = 0;

    this.position = new Vector3();
    this.gravity = new Vector3();

    this.size = new Vector2(10, 10);
    this.sizeStep = new Vector2();
    this.textureIndex = 0;
    this.textureIndexStep = 0;
    this.textureColumns = 1;
    this.textureRows = 1;
    this.color = new Color4(1, 1, 1, 1);
    this.colorStep = new Color4();
    this.velocity = new Vector3();
    this.velocityStep = new Vector3();
    this.rotation = 0;
    this.rotationStep = 0;
  }

  update(_dt: number): boolean {
    const dt = _dt * (ZZ_TICK_PER_SEC / 1000);

    this.age += dt;
    this.eventTimer += dt;

    if (this.age >= this.lifetime) {
      return false;
    }

    this.position.add(this.velocity.clone().multiplyScalar(dt));
    this.velocity.add(this.gravity.clone().multiplyScalar(dt));
    this.color.add(this.colorStep.clone().multiplyScalar(dt));
    this.velocity.add(this.velocityStep.clone().multiplyScalar(dt));
    this.size.add(this.sizeStep.clone().multiplyScalar(dt));
    this.textureIndex += this.textureIndexStep * dt;
    this.rotation += this.rotationStep * dt;

    while (this.rotation >= 360) {
      this.rotation -= 360;
    }

    while (this.rotation < 0) {
      this.rotation += 360;
    }

    this.rootObj.position.copy(this.position);

    this.mesh.scale.x = this.size.x;
    this.mesh.scale.y = this.size.y;
    this.mesh.rotation.z = this.rotation * (Math.PI / 180);
    this.mesh.material.uniforms.vColor.value.setRGB(this.color.r, this.color.g, this.color.b);
    this.mesh.material.uniforms.vAlpha.value = this.color.a;

    // Prevent matrix multiplication errors by hiding
    if (this.size.x === 0 || this.size.y === 0) {
      this.mesh.visible = false;
      this.mesh.scale.set(1, 1, 1);
    } else {
      this.mesh.visible = true;
    }

    if (this.alignType === ParticleSystemData.ALIGN_TYPE.AXIS_ALIGNED) {
      const euler = new Euler();
      euler.setFromQuaternion(camera.quaternion, 'ZXY');
      euler.x = 0;
      euler.y = 0;

      // Rotate to face camera
      this.rootObj.quaternion.setFromEuler(euler);
    } else if (this.alignType === ParticleSystemData.ALIGN_TYPE.BILLBOARD) {
      this.rootObj.quaternion.copy(camera.quaternion);
    }

    if (this.textureRows > 1 || this.textureColumns > 1) {
      const index = Math.floor(this.textureIndex);
      let offsetX = index % this.textureColumns;
      let offsetY = Math.floor(index / this.textureRows);
      offsetX /= this.textureColumns;
      offsetY /= this.textureRows;
      this.mesh.material.uniforms.uvScale.value.set(1 / this.textureColumns, 1 / this.textureRows);
      this.mesh.material.uniforms.uvOffset.value.set(offsetX, offsetY);
    }

    return true;
  }
}

export class ParticleEmitter {
  static Particle = Particle;
  private static _sharedGeometry: BufferGeometry | null = null;

  rootObj: Object3D;
  events: any[];
  particles: Particle[];
  particleCache: Particle[];
  newParticleCounter: number;
  totalParticleLives: number;

  // Properties set externally
  material: any;
  texture: any;
  alignType: any;
  lifeTime: any;
  emitRadius: any;
  gravity: any;
  spriteCols: number = 1;
  spriteRows: number = 1;
  emitRate: any;
  particleCount: number = 0;
  loopCount: number = 0;
  coordType: any;
  spawnDirection: any;

  constructor(name?: string) {
    this.rootObj = new Object3D();
    this.events = [];
    this.particles = [];
    this.particleCache = [];
    this.newParticleCounter = 0;
    this.totalParticleLives = 0;
  }

  private static _getSharedGeometry(): BufferGeometry {
    if (ParticleEmitter._sharedGeometry) {
      return ParticleEmitter._sharedGeometry;
    }

    // Create plane
    const geometry = new PlaneGeometry(1, 1, 1, 1);

    // Flip uv.y
    for (let i = 1; i < geometry.attributes.uv.array.length; i += 2) {
      (geometry.attributes.uv.array as any)[i] = 1 - geometry.attributes.uv.array[i];
    }

    ParticleEmitter._sharedGeometry = geometry;
    return geometry;
  }

  createParticle(): Particle {
    let particle: Particle;

    if (this.particleCache.length > 0) {
      particle = this.particleCache.pop()!;
      particle.reset();
    } else {
      particle = new Particle();

      // Get the geometry
      const sharedGeometry = ParticleEmitter._getSharedGeometry();

      // Create material
      const material = this.material.clone();
      material.uniforms.texture1.value = this.texture;

      particle.mesh = new Mesh(sharedGeometry, material);

      if (this.alignType === ParticleSystemData.ALIGN_TYPE.AXIS_ALIGNED) {
        particle.mesh.rotation.x = Math.PI / 2;
      }

      particle.rootObj.add(particle.mesh);
    }

    this.rootObj.add(particle.rootObj);
    this.totalParticleLives++;
    this.particles.push(particle);

    // Set the initial properties
    particle.lifetime = this.lifeTime.getValueInRange();
    particle.position = this.emitRadius.getValueInRange();
    particle.gravity = this.gravity.getValueInRange();
    particle.textureColumns = this.spriteCols;
    particle.textureRows = this.spriteRows;
    particle.alignType = this.alignType;
    this.applyEvents(particle);
    particle.update(0);

    return particle;
  }

  deleteAllParticles(): void {
    for (let i = 0; i < this.particles.length; ++i) {
      const particle = this.particles[i];
      this.rootObj.remove(particle.rootObj);
      this.particleCache.push(particle);
    }
    this.particles = [];
  }

  update(dt: number): boolean {
    for (let i = 0; i < this.particles.length; ++i) {
      const particle = this.particles[i];

      if (particle.update(dt)) {
        this.applyEvents(particle);
      } else {
        this.particles.splice(i, 1);
        this.rootObj.remove(particle.rootObj);
        this.particleCache.push(particle);
        --i;
      }
    }

    const frameEmitRate = this.emitRate.getValueInRange();
    let numNewParts = Math.floor(frameEmitRate * dt);
    let isRunning = true;

    this.newParticleCounter += (frameEmitRate * dt) - numNewParts;

    if (this.newParticleCounter > 1) {
      numNewParts += Math.floor(this.newParticleCounter);
      this.newParticleCounter -= Math.floor(this.newParticleCounter);
    }

    numNewParts = Math.min(numNewParts, this.particleCount - this.particles.length);

    if (this.loopCount > 0 && this.totalParticleLives + numNewParts > this.loopCount * this.particleCount) {
      numNewParts = (this.loopCount * this.particleCount) - this.totalParticleLives;
      numNewParts = Math.max(0, numNewParts);

      if (numNewParts === 0 && this.particles.length === 0) {
        isRunning = false;
      }
    } else if (this.loopCount > 0 && this.totalParticleLives >= this.loopCount * this.particleCount) {
      if (this.particles.length === 0) {
        isRunning = false;
      }
    }

    for (let i = 0; i < numNewParts; ++i) {
      this.createParticle();
    }

    const parentObject = this.rootObj.parent;

    switch (this.coordType) {
      case ParticleSystemData.COORD_TYPE.LOCAL:
        // Do nothing
        break;
      case ParticleSystemData.COORD_TYPE.WORLD:
        // Do not move with parent
      case ParticleSystemData.COORD_TYPE.LOCAL_WORLD:
        // Reverse parent rotation
        if (parentObject && parentObject.matrixWorld) {
          const rotate = new Quaternion();
          rotate.setFromRotationMatrix(parentObject.matrixWorld);
          rotate.invert();
          this.rootObj.quaternion.copy(rotate);
        }
        break;
    }

    return isRunning;
  }

  applyEvents(particle: Particle): void {
    let dt: number, event: any;

    for (; particle.eventIndex < this.events.length; ++particle.eventIndex) {
      event = this.events[particle.eventIndex];

      if (event.time > particle.eventTimer) {
        break;
      }

      if (event.fadeEvent) {
        dt = event.fadeEvent.time - event.time;
      }

      // Apply events
      switch (event.type) {
        case ParticleSystemData.EVENT_TYPE.SIZE:
          if (!event.fade) {
            particle.size = event.size.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.size.getValueInRange();
            particle.sizeStep.x = (next.x - particle.size.x) / dt!;
            particle.sizeStep.y = (next.y - particle.size.y) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.RED:
          if (!event.fade) {
            particle.color.r = event.red.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.red.getValueInRange();
            particle.colorStep.r = (next - particle.color.r) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.GREEN:
          if (!event.fade) {
            particle.color.g = event.green.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.green.getValueInRange();
            particle.colorStep.g = (next - particle.color.g) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.BLUE:
          if (!event.fade) {
            particle.color.b = event.blue.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.blue.getValueInRange();
            particle.colorStep.b = (next - particle.color.b) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.ALPHA:
          if (!event.fade) {
            particle.color.a = event.alpha.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.alpha.getValueInRange();
            particle.colorStep.a = (next - particle.color.a) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.COLOR:
          if (!event.fade) {
            particle.color = event.color.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.color.getValueInRange();
            particle.colorStep.r = (next.r - particle.color.r) / dt!;
            particle.colorStep.g = (next.g - particle.color.g) / dt!;
            particle.colorStep.b = (next.b - particle.color.b) / dt!;
            particle.colorStep.a = (next.a - particle.color.a) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.VELOCITY_X:
          if (!event.fade) {
            particle.velocity.x = event.velocityX.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.velocityX.getValueInRange();
            particle.velocityStep.x = (next - particle.velocity.x) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.VELOCITY_Y:
          if (!event.fade) {
            particle.velocity.y = event.velocityY.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.velocityY.getValueInRange();
            particle.velocityStep.y = (next - particle.velocity.y) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.VELOCITY_Z:
          if (!event.fade) {
            particle.velocity.z = event.velocityZ.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.velocityZ.getValueInRange();
            particle.velocityStep.z = (next - particle.velocity.z) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.VELOCITY:
          if (!event.fade) {
            particle.velocity = event.velocity.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.velocity.getValueInRange();
            particle.velocityStep.x = (next.x - particle.velocity.x) / dt!;
            particle.velocityStep.y = (next.y - particle.velocity.y) / dt!;
            particle.velocityStep.z = (next.z - particle.velocity.z) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.TEXTURE:
          if (!event.fade) {
            particle.textureIndex = event.textureIndex.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.textureIndex.getValueInRange();
            particle.textureIndexStep = (next - particle.textureIndex) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.ROTATION:
          if (!event.fade) {
            particle.rotation = event.rotation.getValueInRange();
          }

          if (event.fadeEvent) {
            const next = event.fadeEvent.rotation.getValueInRange();
            particle.rotationStep = (next - particle.rotation) / dt!;
          }
          break;
        case ParticleSystemData.EVENT_TYPE.TIMER:
          particle.eventTimer = event.timer.getValueInRange();

          // Find new eventIndex
          particle.eventIndex = 0;

          for (; particle.eventIndex < this.events.length; particle.eventIndex++) {
            if (this.events[particle.eventIndex].actualTime >= particle.eventTimer) {
              break;
            }
          }

          particle.eventIndex--; // because outer loop ++
          break;
      }
    }
  }
}
